/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import { IAdminClozeData } from '@datatype/admin/api-type';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import TextButton from '@design-system/text-button';

interface IProps {
  isOpen: boolean;
  cloze: IAdminClozeData | null;
  onClose: () => void;
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background-color: ${color.MODAL_COLOR};
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;
  width: 100%;
  max-width: 480px;
  background-color: ${color.WHITE_1};
  padding: 32px 24px 24px;
  border-radius: 12px;
  box-shadow: ${color.SURFACE_SHADOW};
`;

const CloseButton = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const Section = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 6px;
`;

const ScoreBox = styled.div`
  padding: 10px 12px;
  background-color: ${color.GRAY_5};
  border-radius: 6px;
`;

const CommentBox = styled.div`
  padding: 10px 12px;
  background-color: ${color.GRAY_5};
  border-radius: 6px;
  max-height: 240px;
  overflow-y: auto;
  white-space: pre-wrap;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  margin-top: 8px;
`;

const ClozeReviewModal: React.FC<IProps> = ({ isOpen, cloze, onClose }) => {
  if (!isOpen) return null;

  const score = cloze?.reviewScore ?? null;
  const comment = cloze?.reviewComment ?? null;

  return (
    <Backdrop onClick={onClose}>
      <Card onClick={(ev) => ev.stopPropagation()}>
        <CloseButton>
          <IconBlock
            icon='close'
            design={{
              icon: { size: 'medium', color: color.BLACK_4 },
              box: { event: { cursor: 'pointer' } },
            }}
            click={{ handler: onClose }}
          />
        </CloseButton>
        <TextBlock
          text='검수 결과'
          design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
        />
        <Section>
          <TextBlock text='Score' design={{ text: { size: 'small', color: color.BLACK_5 } }} />
          <ScoreBox>
            <TextBlock
              text={score != null ? String(score) : '-'}
              design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }}
            />
          </ScoreBox>
        </Section>
        <Section>
          <TextBlock text='Comment' design={{ text: { size: 'small', color: color.BLACK_5 } }} />
          <CommentBox>
            <TextBlock
              text={comment ?? '-'}
              design={{ text: { size: 'medium', color: color.BLACK_2 } }}
            />
          </CommentBox>
        </Section>
        <ButtonRow>
          <TextButton text='닫기' theme={ETheme.GRAY} kind='outlined' width='120px' handleClick={onClose} />
        </ButtonRow>
      </Card>
    </Backdrop>
  );
};

export default ClozeReviewModal;
